import {
    CardGrid,
    ContentSection,
    LinkRow,
    PageFrame,
    PageHero,
} from '../components/PageBlocks';
import { CLAIM_TRUTH_CLASSES, getPageConfig } from '../data/page-config.mjs';

const page = getPageConfig('/use-cases');

const UseCasesPage = () => {
    return (
        <PageFrame>
            <PageHero
                sectionId="overview"
                variant="numbered"
                numeral="V"
                eyebrow="Primary Users"
                title={page.h1}
                intro="AvenBox is built for work where the routing question is not incidental — it is structural. This is not AI for everyone. It is AI for work that cannot afford invisible routing."
                primaryCta={{ label: 'Reserve Position', to: '/contact' }}
                secondaryCta={{ label: 'Contact', to: '/contact', variant: 'secondary' }}
                proofStrip={[
                    'Privacy-sensitive professionals first.',
                    'Founder and operator workflows.',
                    'Technical operators who evaluate architecture, not claims.',
                ]}
            />

            <ContentSection
                sectionId="who-benefits-first"
                eyebrow="Who Benefits First"
                title="Work where routing cannot be invisible."
                intro="Cloud AI answers the routing question the same way for every user: outward, by default. AvenBox is for work where that answer is unacceptable."
                borderTop={false}
            >
                <CardGrid
                    pattern="meta"
                    columns={2}
                    items={[
                        {
                            title: 'Privacy-sensitive professionals',
                            body: 'AvenBox fits work where notes, documents, private reasoning, and internal context should remain under user control rather than becoming outside system context by default.',
                            list: [
                                'Internal research and sensitive reading.',
                                'Private drafting and working notes.',
                                'Document-heavy reasoning under real trust constraints.',
                            ],
                        },
                        {
                            title: 'Founders and operators',
                            body: 'Strategic work — planning, decision support, synthesis — moves across surfaces and cannot route through cloud AI by default without losing control over what gets exposed. AvenBox is built for that continuity without that exposure.',
                            list: [
                                'Planning and synthesis across surfaces.',
                                'Private decision support.',
                                'Continuity between direct use and desk workflows.',
                            ],
                        },
                        {
                            title: 'Developers and technical operators',
                            body: 'Developers and technical operators understand the routing problem directly. They can see what happens when context leaves a local boundary — and evaluate whether the architecture actually enforces the rules it claims.',
                            list: [
                                'Local reasoning over sensitive code or docs.',
                                'Controlled extension to trusted systems.',
                                'A clear model of where intelligence is actually running.',
                            ],
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ROADMAP,
                            title: 'Later enterprise and security direction',
                            body: 'Enterprise and security-conscious organisations are a later direction. The control architecture AvenBox is being built around is compatible with those requirements — but that use case belongs to a later product stage, not the current prototype.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="concrete-benefit"
                eyebrow="What You Gain"
                title="Concrete benefit, not generic privacy language."
                intro="The value of AvenBox is specific to the work. Each user type gains something the cloud-default path cannot deliver without compromise."
            >
                <div className="ed-benefit">
                    <div className="ed-benefit__row">
                        <div className="ed-benefit__num">01</div>
                        <div className="ed-benefit__label">Privacy-sensitive professionals</div>
                        <div className="ed-benefit__gain">
                            <em>Confidential material stays confidential by default.</em>
                            <span>No silent routing, no accidental context leakage, no policy toggles to manage. The trust boundary is the system, not the settings.</span>
                        </div>
                    </div>
                    <div className="ed-benefit__row">
                        <div className="ed-benefit__num">02</div>
                        <div className="ed-benefit__label">Founders and operators</div>
                        <div className="ed-benefit__gain">
                            <em>Strategic thinking that survives surface changes.</em>
                            <span>Planning, synthesis, and decision support keep continuity across direct use and desk work — without the context itself becoming cloud-provider material.</span>
                        </div>
                    </div>
                    <div className="ed-benefit__row">
                        <div className="ed-benefit__num">03</div>
                        <div className="ed-benefit__label">Developers and technical operators</div>
                        <div className="ed-benefit__gain">
                            <em>A control model you can actually inspect.</em>
                            <span>Routing is explicit and visible. You can verify where intelligence runs, not trust a vendor claim. Sensitive code and docs stay inside the boundary you define.</span>
                        </div>
                    </div>
                </div>
            </ContentSection>

            <ContentSection
                sectionId="before-after"
                eyebrow="Before / After"
                title="The routing question, made visible."
                intro="Most AI tools answer the routing question by default — outward. AvenBox makes that decision structural and explicit."
            >
                <div className="ed-split">
                    <div className="ed-split__col ed-split__col--before">
                        <div className="ed-split__tag">Cloud default</div>
                        <div className="ed-split__title">Routing is invisible.</div>
                        <ul className="ed-split__list">
                            <li>Private context leaves the device by default.</li>
                            <li>The user has to work against the system to keep material local.</li>
                            <li>Continuity comes packaged with silent outward routing.</li>
                            <li>Control lives in settings — not in the architecture.</li>
                        </ul>
                    </div>
                    <div className="ed-split__divider" aria-hidden="true">
                        <span>vs</span>
                    </div>
                    <div className="ed-split__col ed-split__col--after">
                        <div className="ed-split__tag ed-split__tag--accent">AvenBox</div>
                        <div className="ed-split__title">Routing is the product.</div>
                        <ul className="ed-split__list">
                            <li>Local execution is the default path.</li>
                            <li>Extension is explicit, bounded, and user-governed.</li>
                            <li>Continuity works without spending trust to get it.</li>
                            <li>Control is enforced at the architecture level, not via toggles.</li>
                        </ul>
                    </div>
                </div>
            </ContentSection>

            <ContentSection
                sectionId="why-cloud-defaults-fail"
                eyebrow="Why Cloud Defaults Fail"
                title="Cloud AI is optimized for the use case where routing does not matter."
                intro="AvenBox is for the use case where it does. These are not edge cases — they are the conditions under which serious professional work operates."
            >
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            title: 'Cloud convenience hides the real cost',
                            body: 'Most AI workflows are designed for ease of access, not control. Private material routes outward by default — and the user has to actively work against the system to prevent it.',
                        },
                        {
                            title: 'Continuity often comes with trust loss',
                            body: 'Users can get convenience today, but usually by accepting opaque routing and weaker control over where their context travels across sessions and surfaces.',
                        },
                        {
                            title: 'The control layer is the missing piece',
                            body: 'AvenBox is being built as the layer between user and execution — where routing is visible, explicit, and under the user\'s authority across device, app, and workstation.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="read-path"
                eyebrow="Read Path"
                title="The architecture is designed for exactly this problem."
            >
                <LinkRow
                    links={[
                        { label: 'AvenBox', to: '/avenbox' },
                        { label: 'Technology', to: '/technology' },
                        { label: 'Security', to: '/security' },
                        { label: 'Pricing', to: '/pricing' },
                    ]}
                />
            </ContentSection>
        </PageFrame>
    );
};

export default UseCasesPage;
